import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MenuFallback from "../../json/menu_admin_new.json";
import { FaChevronDown } from 'react-icons/fa';
import { renderMenuIcon } from '../../helper/menuIcons';
import { findActiveParentIndex, hasActiveDescendant, isMenuItemActive } from '../../helper/menuActive';
import { NavLink, useLocation } from 'react-router-dom';

const linkClass = (isActive, isTopLevel) => {
    const size = isTopLevel ? 'nav-item-link-top' : 'nav-item-link-sub';
    const state = isActive ? 'nav-item-active' : 'nav-item';
    return `${size} ${state}`;
};

const NestedMenu = ({ menu, setShowSidebar, depth = 0 }) => {
    const location = useLocation();
    const [openIndex, setOpenIndex] = useState(() => {
        const activeIdx = findActiveParentIndex(menu, location.pathname);
        return activeIdx >= 0 ? activeIdx : null;
    });

    useEffect(() => {
        const activeIdx = findActiveParentIndex(menu, location.pathname);
        if (activeIdx >= 0) {
            setOpenIndex(activeIdx);
        }
    }, [location.pathname, menu]);

    const toggleSubMenu = (index, hasPath) => {
        if (hasPath) {
            setShowSidebar('-left-72');
        }
        setOpenIndex((prev) => (prev === index ? null : index));
    };

    const isTopLevel = depth === 0;

    return (
        <ul className="space-y-0.5">
            {menu.map((item, index) => {
                const hasChildren = item.sub && item.sub.length > 0;
                const childActive = hasChildren && item.sub.some((sub) =>
                    hasActiveDescendant(sub, location.pathname)
                );
                const isOpen = openIndex === index;

                return (
                    <li key={`${item.menu_key || item.name}-${index}`}>
                        {item.path ? (
                            <NavLink
                                to={item.path}
                                end={item.path === '/admin/dashboard' || item.path === '/admin'}
                                className={({ isActive }) =>
                                    linkClass(
                                        isActive || isMenuItemActive(item, location.pathname),
                                        isTopLevel
                                    )
                                }
                                onClick={() => toggleSubMenu(index, true)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="nav-item-icon text-sm">
                                        {renderMenuIcon(item.icon)}
                                    </span>
                                    <span>{item.name}</span>
                                </div>
                                {hasChildren ? (
                                    <FaChevronDown
                                        className={`text-[10px] opacity-50 transition-transform duration-tesla [.nav-item-active_&]:opacity-80 ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                ) : null}
                            </NavLink>
                        ) : (
                            <div
                                title={item.name}
                                className={`${isTopLevel ? 'nav-item-link-top' : 'nav-item-link-sub'} cursor-pointer ${childActive ? 'nav-item-active' : 'nav-item'}`}
                                onClick={() => toggleSubMenu(index, false)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="nav-item-icon text-sm">
                                        {renderMenuIcon(item.icon)}
                                    </span>
                                    <span>{item.name}</span>
                                </div>
                                {hasChildren ? (
                                    <FaChevronDown
                                        className={`text-[10px] opacity-50 transition-transform duration-tesla [.nav-item-active_&]:opacity-80 ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                ) : null}
                            </div>
                        )}

                        <AnimatePresence initial={false}>
                            {hasChildren && isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.18, ease: "easeOut" }}
                                    className="ml-3 overflow-hidden border-l border-accent-brand/25 pl-2 dark:border-white/10"
                                >
                                    <NestedMenu
                                        menu={item.sub}
                                        setShowSidebar={setShowSidebar}
                                        depth={depth + 1}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </li>
                );
            })}
        </ul>
    );
};

const Menus = ({ setShowSidebar, menuData }) => {
    return <NestedMenu menu={menuData || MenuFallback} setShowSidebar={setShowSidebar} depth={0} />;
};

export default Menus;
